import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { routerActions } from '@opengov/compose-app-router/state';
import { createSelector } from '@opengov/compose-app/reselect';
import { IEntity } from '@opengov/og-api-ts';
import { STORIES_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { StoriesSettings } from '../components';
import { fetchCurrentSubjectClaims } from '../api/subjectClaimsApi';
import { selectStoriesSettingsProps } from '../selectors';
import { getSession } from '@opengov/app-core/session/selectors';
import { hasPrivilege } from '../utils';
import { ISubjectClaims } from '../../types';

export const storiesSettingsRoute = {
  name: 'StoriesSettings',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Settings', name)),
  selector: selectStoriesSettingsProps,
  component: StoriesSettings,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation, onSuccess, onError) => {
      const { entitySubdomain } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          const currentSubjectClaims = await fetchCurrentSubjectClaims({
            userUuid: getSession(state).uuid,
            entityUuid: entity.uuid
          });

          const isAdmin = hasPrivilege(currentSubjectClaims as ISubjectClaims, STORIES_ADMIN_PRIVILEGE);

          if (!isAdmin) {
            return onError(
              'User does not have permission...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }

          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};
